import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
const STATUSES = ['Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled'];
const RECORD_TYPES = ['Medical', 'Billing', 'Imaging', 'All Records'];

const STATUS_COLORS = {
  'Pending': 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-yellow-100 text-yellow-800',
  'Completed': 'bg-green-100 text-green-800',
  'On Hold': 'bg-orange-100 text-orange-800',
  'Cancelled': 'bg-red-100 text-red-800',
};

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [wo, setWo] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/work-orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWo(res.data);
        setForm(res.data);
      } catch (err) {
        console.error('Failed to load work order', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, token]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/work-orders/${id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API_URL}/api/work-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWo(res.data);
      setForm(res.data);
    } catch (err) {
      console.error('Failed to save', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-500">Loading...</div>;
  if (!wo) return <div className="p-8 text-red-500">Work order not found</div>;

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Work Order: {wo.wo_number}
          <span className={`ml-3 px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[wo.status] || ''}`}>{wo.status}</span>
          {wo.rush_order && <span className="ml-2 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">RUSH</span>}
        </h1>
        <button onClick={() => navigate(`/causes/${wo.cause_id}`)} className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Back to Case</button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700 border-b pb-1">Stop Location</h3>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Name</label>
              <input value={form.stop_location_name || ''} onChange={(e) => set('stop_location_name', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Additional Name</label>
              <input value={form.stop_location_additional_name || ''} onChange={(e) => set('stop_location_additional_name', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Address</label>
              <input value={form.stop_location_address || ''} onChange={(e) => set('stop_location_address', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">City/State/Zip</label>
              <input value={form.stop_location_city_state_zip || ''} onChange={(e) => set('stop_location_city_state_zip', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Phone</label>
              <input value={form.stop_location_phone || ''} onChange={(e) => set('stop_location_phone', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Contact</label>
              <input value={form.stop_location_contact || ''} onChange={(e) => set('stop_location_contact', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700 border-b pb-1">Record Details</h3>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Status</label>
              <select value={form.status || ''} onChange={(e) => set('status', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded bg-white">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Type of Record</label>
              <select value={form.type_of_record || ''} onChange={(e) => set('type_of_record', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded bg-white">
                <option value="">-- Select --</option>
                {RECORD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Date From</label>
                <input type="date" value={form.record_date_from || ''} onChange={(e) => set('record_date_from', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Date To</label>
                <input type="date" value={form.record_date_to || ''} onChange={(e) => set('record_date_to', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Legal Tech</label>
              <input value={form.legal_tech || ''} onChange={(e) => set('legal_tech', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Records Requested</label>
              <textarea value={form.records_requested || ''} onChange={(e) => set('records_requested', e.target.value)} rows={3} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Notes</label>
              <textarea value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border rounded" />
            </div>
            <label className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded ${form.rush_order ? 'bg-red-100 text-red-800' : 'text-gray-700'}`}>
              <input type="checkbox" checked={form.rush_order || false} onChange={(e) => set('rush_order', e.target.checked)} />
              Rush Order
            </label>
          </div>
        </div>

        <div className="flex gap-2 mt-6 pt-4 border-t">
          <button onClick={save} disabled={saving} className="px-4 py-2 text-white rounded text-sm font-medium" style={{ backgroundColor: '#1a2744' }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button onClick={() => navigate(`/causes/${wo.cause_id}`)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300">
            Back to Case
          </button>
        </div>
      </div>
    </div>
  );
}
